import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { devWarn } from "@/lib/logger";

/**
 * Iter-63.1.6 — Searchable organization picker.
 *
 * Replaces the raw <select> used in InviteEmployee for the client / affiliate
 * org selector. The legacy backend endpoint `/api/organizations?type=X`
 * returns only the first 500 rows alphabetically (see
 * `backend/routes/org_routes.py:170`), so orgs whose names sort later than
 * position 500 were unreachable in the UI when the seeded / test-org
 * population is large.
 *
 * This component uses the same endpoint's Iter-61.4 paginated shape
 * (`?q=&page_size=&page=`) so any org is discoverable by typing 2+ chars.
 * Debounced 250 ms to avoid flooding the backend, cancels stale in-flight
 * fetches, and preserves the display name for an already-selected org that
 * isn't in the current query result.
 *
 * Contract:
 *   - `type`     : "client" | "affiliate"
 *   - `value`    : org_id (string) — controlled
 *   - `onChange` : (org_id) => void
 *   - `required` : native form-required behaviour via a hidden shadow input
 *   - `testId`   : data-testid on the visible search input (default "org-picker")
 *
 * No routing / RBAC change — pure client-side UX for an endpoint that
 * already supports search + pagination.
 */
export default function OrgPicker({
  type,
  value,
  onChange,
  required = false,
  disabled = false,
  testId = "org-picker",
  placeholder,
}) {
  const [query, setQuery] = useState("");
  const [debounced, setDebounced] = useState("");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [selectedName, setSelectedName] = useState("");

  const wrapRef = useRef(null);

  // Debounce typing.
  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), 250);
    return () => clearTimeout(t);
  }, [query]);

  // Fetch page whenever debounced query or type changes.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const params = { type, page_size: 50, page: 1 };
        if (debounced) params.q = debounced;
        const { data } = await api.get("/organizations", { params });
        if (cancelled) return;
        setItems(data.organizations || []);
        setTotal(data.pagination?.total ?? (data.organizations || []).length);
      } catch (err) {
        if (!cancelled) {
          devWarn("OrgPicker: fetch failed", err);
          setItems([]);
          setTotal(0);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [type, debounced]);

  // Resolve display name for an already-selected value that's not in `items`.
  useEffect(() => {
    if (!value) { setSelectedName(""); return; }
    const hit = items.find((o) => o.id === value);
    if (hit) { setSelectedName(hit.name); return; }
    if (selectedName) return; // don't re-fetch if we already have a name
    let cancelled = false;
    api.get(`/organizations/${value}`)
      .then((r) => { if (!cancelled) setSelectedName(r.data?.name || ""); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [value, items]); // eslint-disable-line react-hooks/exhaustive-deps

  // Close dropdown on outside click.
  useEffect(() => {
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const pick = (org) => {
    onChange?.(org.id);
    setSelectedName(org.name);
    setQuery("");
    setOpen(false);
  };

  const clear = () => {
    onChange?.("");
    setSelectedName("");
    setQuery("");
  };

  // Display string: what shows in the input when it's not being edited.
  const displayValue = open ? query : (selectedName || query);

  return (
    <div ref={wrapRef} className="relative">
      <input
        type="text"
        data-testid={testId}
        value={displayValue}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        disabled={disabled}
        autoComplete="off"
        placeholder={placeholder || `Search ${type} org…`}
        className="w-full border border-black px-3 py-2 mt-1"
        aria-expanded={open}
        aria-controls={`${testId}-listbox`}
      />
      {/* Shadow input carries the org_id + native `required` validity so the
          surrounding <form> still gates submit correctly. */}
      <input
        type="text"
        tabIndex={-1}
        aria-hidden="true"
        required={required}
        value={value || ""}
        onChange={() => {}}
        data-testid={`${testId}-hidden`}
        className="sr-only"
        style={{
          position: "absolute", opacity: 0, height: 0, width: 0, border: 0,
          padding: 0, pointerEvents: "none",
        }}
      />
      {value && !open && (
        <button
          type="button"
          onClick={clear}
          data-testid={`${testId}-clear`}
          className="absolute right-2 top-[calc(50%+2px)] -translate-y-1/2 text-[10px] uppercase tracking-widest text-zinc-500 hover:text-black"
        >
          Clear
        </button>
      )}
      {open && (
        <div
          id={`${testId}-listbox`}
          role="listbox"
          data-testid={`${testId}-list`}
          className="absolute z-50 mt-1 max-h-72 w-full overflow-y-auto border border-black bg-white brut-shadow-sm"
        >
          {loading && (
            <div className="px-3 py-2 text-[11px] uppercase tracking-widest text-zinc-500" data-testid={`${testId}-loading`}>
              Loading…
            </div>
          )}
          {!loading && items.length === 0 && (
            <div className="px-3 py-2 text-[11px] uppercase tracking-widest text-zinc-500" data-testid={`${testId}-empty`}>
              {debounced ? `No ${type} orgs match “${debounced}”` : `No ${type} orgs found`}
            </div>
          )}
          {!loading && items.map((o) => (
            <button
              type="button"
              key={o.id}
              onClick={() => pick(o)}
              data-testid={`${testId}-opt-${o.id}`}
              className={`w-full text-left px-3 py-2 text-sm hover:bg-black hover:text-white ${value === o.id ? "bg-black text-white" : ""}`}
            >
              <div className="truncate">{o.name}</div>
              {o.contact_email && (
                <div className={`text-[10px] uppercase tracking-widest truncate ${value === o.id ? "text-zinc-300" : "text-zinc-500"}`}>
                  {o.contact_email}
                </div>
              )}
            </button>
          ))}
          {!loading && total > items.length && (
            <div className="px-3 py-2 text-[10px] uppercase tracking-widest text-zinc-500 border-t border-black" data-testid={`${testId}-more`}>
              Showing {items.length} of {total} · type more to narrow
            </div>
          )}
        </div>
      )}
    </div>
  );
}
